import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Input, Table, InputNumber, Popover, Select, Tag } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import { SearchOutlined, PlusOutlined, PrinterOutlined, DeleteOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { DashboardLayout } from '../layouts/DashboardLayout';
import {
  searchRegistrableCards,
  fetchBuybackListings,
  fetchBuybackListingDates,
  addBuybackListing,
  updateBuybackListingPrice,
  deleteBuybackListing,
} from '../api/api';
import { RegistrableGameType, RegistrableSearchResult, GAME_TYPE_LABEL } from '../types/productRegistration';
import { BuybackListing } from '../types/buybackListing';
import { SPACING, FONT_SIZE, RADIUS, SHADOW } from '../constants/constants';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { AppButton } from '../components/common/AppButton';
import { AppLoadError } from '../components/common/AppLoadError';
import { printBuybackList } from '../utils/buybackListingPrint';

const GAME_OPTIONS: { label: string; value: RegistrableGameType }[] =
  (['POKEMON', 'ONE_PIECE', 'DIGIMON'] as RegistrableGameType[])
    .map((value) => ({ value, label: GAME_TYPE_LABEL[value] }));

const errMessage = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

/**
 * 고유번호 표시값 = 확장팩 코드 + 카드번호 (예: "M1-069"). 카드번호만(예: "069")으로는
 * 어느 확장팩인지 알 수 없어, 담을 때 확장팩 코드를 앞에 붙여 저장한다. 확장팩 코드가
 * 없으면 카드번호만 쓴다.
 */
const uniqueNo = (c: { expansionCode: string | null; cardNumber: string }): string =>
  c.expansionCode ? `${c.expansionCode}-${c.cardNumber}` : c.cardNumber;

/**
 * 매입 리스트 — 직원이 상품을 검색해 "삽니다"로 선정하고, 가격을 매겨 게시하는 화면.
 * 기존 "매입 관리"(손님 접수 오더)와는 다른 개념이다.
 *
 * 메인: 상품 검색(기존 searchRegistrableCards 재사용) → 선정해 담기 → DB 저장(이미지·상품명·
 * 고유번호·가격). 가격은 목록에서 인라인으로 수시 수정. 서브: A4 인쇄로 손님용 게시물 출력
 * (인쇄물은 이미지가 주인공). 매장별로 관리된다(백엔드 라우팅). 지금은 포켓몬 위주지만
 * 게임 선택은 열어 둔다(이후 확장).
 */
export const BuybackListPage: React.FC = () => {
  const t = useThemeTokens();
  const [gameType, setGameType] = useState<RegistrableGameType>('POKEMON');

  // ── 검색
  const [searchDraft, setSearchDraft] = useState('');
  const [searchResults, setSearchResults] = useState<RegistrableSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [addedCodes, setAddedCodes] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── 리스트(게임·날짜별). 백엔드가 발매일 내림차순 → 카드번호 오름차순으로 정렬해 준다.
  const [listings, setListings] = useState<BuybackListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // ── 날짜 축. availableDates 는 최신순(오늘이 항상 [0]). selectedDate 가 오늘이 아니면
  // 과거 이력(읽기 전용)이다 — 담기/가격 수정/삭제를 막는다.
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const todayStr = availableDates[0] ?? '';
  const isReadOnly = !!selectedDate && !!todayStr && selectedDate !== todayStr;

  // 날짜 목록 · 목록 조회는 늦게 끝난 옛 응답이 새 응답을 덮지 않게 차례 번호로 가린다 — 과거 날짜 조회가 오늘 조회보다
  // 늦게 끝나면 오늘 화면(수정 · 삭제 가능) 아래에 과거 목록이 깔렸다(2026-10-22 코덱 전체 점검, 감사 card-controltower#106).
  const datesSeqRef = useRef(0);
  const listSeqRef = useRef(0);
  // 날짜 목록 조회가 실패했는지 — 실패하면 selectedDate 가 빈 채로 남아 목록 조회가 시작되지 않고 로딩만 돌았다(#106).
  const [datesFailed, setDatesFailed] = useState(false);

  // 게임이 바뀌면 그 게임의 날짜 목록을 다시 불러오고, 선택 날짜를 오늘로 되돌린다.
  const loadDates = useCallback(() => {
    const seq = ++datesSeqRef.current;
    setDatesFailed(false);
    setError(null);
    fetchBuybackListingDates(gameType)
      .then((dates) => {
        if (seq !== datesSeqRef.current) return;
        setAvailableDates(dates);
        setSelectedDate(dates[0] ?? '');
        // 날짜가 하나도 없으면 목록 조회가 시작되지 않는다 — 로딩을 여기서 끈다.
        if (!dates[0]) setLoading(false);
      })
      .catch((e) => {
        if (seq !== datesSeqRef.current) return;
        message.error(errMessage(e, '날짜 목록을 불러오지 못했습니다'));
        setDatesFailed(true);
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      });
  }, [gameType]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  const loadListings = useCallback(() => {
    if (!selectedDate) {
      // 날짜가 비면(게임 전환 · 날짜 다시 읽기) 앞 조회를 무르고 목록도 비운다(Opus 리뷰 PR #51 낮음).
      ++listSeqRef.current;
      setListings([]);
      return;
    }
    const seq = ++listSeqRef.current;
    setLoading(true);
    setError(null);
    fetchBuybackListings(gameType, selectedDate)
      .then((rows) => {
        if (seq === listSeqRef.current) setListings(rows);
      })
      .catch((e) => {
        if (seq !== listSeqRef.current) return;
        message.error(errMessage(e, '매입 리스트를 불러오지 못했습니다'));
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (seq === listSeqRef.current) setLoading(false);
      });
  }, [gameType, selectedDate]);
  // 담기 · 가격 수정 뒤 다시 읽기는 클릭 때의 함수가 아니라 지금(게임 · 날짜) 함수로 — 그 사이 게임 · 날짜를 바꾸면 옛 함수가
  // 차례 번호를 올려 새 선택의 응답을 버리고 옛 목록을 남겼다(Opus 리뷰 PR #51 중간, #106 과 같은 꼴).
  const loadListingsRef = useRef(loadListings);
  loadListingsRef.current = loadListings;

  // 다시 시도 — 날짜 목록부터 실패했으면 날짜부터(선택 날짜를 비워 두어, 날짜가 오면 같은 날짜여도 목록을 다시 읽는다),
  // 아니면 목록만.
  const retryLoadRef = useRef<() => void>(() => {});
  const retryLoad = useCallback(() => {
    if (datesFailed || !selectedDate) {
      setSelectedDate('');
      setLoading(true);
      loadDates();
    } else {
      loadListings();
    }
  }, [datesFailed, selectedDate, loadDates, loadListings]);
  retryLoadRef.current = retryLoad;

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchDraft(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      searchRegistrableCards(gameType, value.trim())
        .then(setSearchResults)
        .catch((e) => message.error(errMessage(e, '검색에 실패했습니다')))
        .finally(() => setSearching(false));
    }, 350);
  }, [gameType]);

  const handleAdd = useCallback(async (r: RegistrableSearchResult) => {
    const c = r.candidate;
    try {
      await addBuybackListing({
        gameType,
        shopProductCode: c.shopProductCode,
        cardNumber: uniqueNo(c),
        expansionCode: c.expansionCode,
        productName: c.name,
        imageUrl: c.imageUrl,
      });
      setAddedCodes((prev) => new Set(prev).add(c.shopProductCode));
      // 목록 조회가 실패해 AppLoadError 가 화면을 덮은 상태에서도 담기는 성공할 수 있다 —
      // 그때 오류를 그대로 두면 오류 화면이 목록을 가린 채 건수만 늘어난다(2026-10-20 리뷰).
      setError(null);
      // 백엔드가 발매일·카드번호로 정렬해 주므로, 낙관적 prepend 대신 다시 불러와 정렬을 유지한다. 날짜 목록부터 실패해 선택
      // 날짜가 없으면 날짜부터 다시 읽는다 — 목록 조회가 안 나가 담은 상품이 빈 목록으로 보이고 다시 시도도 사라졌다(코덱 리뷰
      // #209-6, 감사 card-controltower#286).
      retryLoadRef.current();
      message.success('매입 리스트에 담았습니다');
    } catch (e) {
      message.error(errMessage(e, '담기에 실패했습니다'));
    }
  }, [gameType]);

  const handlePriceChange = useCallback(async (id: number, price: number | null) => {
    try {
      const updated = await updateBuybackListingPrice(id, price);
      setListings((prev) => prev.map((it) => (it.id === id ? updated : it)));
    } catch (e) {
      message.error(errMessage(e, '가격 수정에 실패했습니다'));
      loadListingsRef.current(); // 실패 시 서버 값으로 되돌린다
    }
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    try {
      await deleteBuybackListing(id);
      setListings((prev) => prev.filter((it) => it.id !== id));
      message.success('삭제했습니다');
    } catch (e) {
      message.error(errMessage(e, '삭제에 실패했습니다'));
    }
  }, []);

  const handlePrint = useCallback(() => {
    if (listings.length === 0) {
      message.info('인쇄할 항목이 없습니다');
      return;
    }
    const ok = printBuybackList(listings);
    if (!ok) message.error('팝업이 차단되어 인쇄 창을 열지 못했습니다. 팝업 허용 후 다시 시도해 주세요.');
  }, [listings]);

  // 썸네일. 이미지가 있으면 마우스를 올렸을 때 카드 이미지를 크게 확대해 보여 준다
  // (직원이 작은 썸네일만으로는 카드를 구분하기 어렵다는 점 — 목록·검색 결과 공통).
  const thumb = useCallback((url: string | null, alt: string, size: number) => {
    if (!url) {
      return <div style={{ width: size, height: size, borderRadius: RADIUS.sm, background: t.bgPage, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: FONT_SIZE.xs, color: t.textMuted }}>—</div>;
    }
    return (
      <Popover
        trigger="hover"
        placement="right"
        mouseEnterDelay={0.15}
        content={<img src={url} alt={alt} style={{ width: 260, maxHeight: 360, objectFit: 'contain', display: 'block' }} />}
      >
        <img
          src={url}
          alt={alt}
          style={{ width: size, height: size, objectFit: 'contain', borderRadius: RADIUS.sm, background: t.bgPage, cursor: 'zoom-in' }}
        />
      </Popover>
    );
  }, [t]);

  const searchColumns: ColumnsType<RegistrableSearchResult> = useMemo(() => [
    {
      title: '이미지', width: 64,
      render: (_, r) => thumb(r.candidate.imageUrl, r.candidate.name, 40),
    },
    {
      title: '카드명', dataIndex: ['candidate', 'name'],
      render: (_, r) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{r.candidate.name}</span>,
    },
    {
      title: '고유번호', width: 130,
      render: (_, r) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>{uniqueNo(r.candidate)}</span>,
    },
    {
      title: '확장팩', width: 150,
      render: (_, r) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>{r.candidate.expansionName}</span>,
    },
    {
      title: '', width: 96,
      render: (_, r) => {
        const added = addedCodes.has(r.candidate.shopProductCode);
        return (
          <AppButton
            variant="default"
            size="sm"
            icon={<PlusOutlined />}
            disabled={added}
            disabledReason={added ? '이미 담김' : undefined}
            onClick={() => handleAdd(r)}
          >
            {added ? '담김' : '담기'}
          </AppButton>
        );
      },
    },
  ], [t, thumb, addedCodes, handleAdd]);

  const listColumns: ColumnsType<BuybackListing> = useMemo(() => [
    {
      title: '이미지', width: 72,
      render: (_, it) => thumb(it.imageUrl, it.productName, 48),
    },
    {
      title: '상품명', dataIndex: 'productName',
      render: (v: string) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{v}</span>,
    },
    {
      title: '고유번호', dataIndex: 'cardNumber', width: 140,
      render: (v: string | null) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>{v ?? '-'}</span>,
    },
    {
      title: '매입가', width: 180,
      render: (_, it) => (
        <InputNumber
          value={it.price}
          min={0}
          step={100}
          disabled={isReadOnly}
          style={{ width: 150 }}
          placeholder="미정"
          formatter={(v) => (v == null ? '' : `${Number(v).toLocaleString('ko-KR')}원`)}
          parser={(v) => (v ? Number(v.replace(/[^\d]/g, '')) : 0)}
          onBlur={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, '');
            const next = raw === '' ? null : Number(raw);
            if (next !== it.price) handlePriceChange(it.id, next);
          }}
          onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
        />
      ),
    },
    {
      title: '', width: 64,
      // 삭제는 확인 모달 없이 즉시 실행한다(사용자 요청, 2026-09-11). 과거 날짜(이력)는 삭제 불가.
      render: (_, it) => (
        isReadOnly ? null :
        <AppButton variant="ghost-danger" size="sm" icon={<DeleteOutlined />} iconOnly aria-label="삭제"
          onClick={() => handleDelete(it.id)} />
      ),
    },
  ], [t, thumb, handlePriceChange, handleDelete, isReadOnly]);

  const card = (children: React.ReactNode, extra?: React.CSSProperties): React.ReactNode => (
    <div style={{
      background: t.bgSurface,
      border: `1px solid ${t.border}`,
      borderRadius: RADIUS.lg,
      boxShadow: SHADOW.card,
      padding: SPACING.xl,
      ...extra,
    }}>
      {children}
    </div>
  );

  const dateLabel = (d: string): string => (d === todayStr ? `오늘 (${d})` : d);

  return (
    <DashboardLayout title="매입 리스트">
      {/* 게임(장르) 선택 — 검색과 리스트 모두 이 게임 기준으로 좁혀진다. */}
      {card(
        <>
          <p style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, marginTop: 0, marginBottom: SPACING.md }}>
            매입 리스트는 게임(장르)별·날짜별로 관리됩니다. 오늘 리스트는 전날 리스트를 그대로 이어받아 열리며, 가격 변동만 수정하면 됩니다.
          </p>
          <Segmented
            value={gameType}
            onChange={(v) => { setGameType(v as RegistrableGameType); setSearchResults([]); setSearchDraft(''); }}
            options={GAME_OPTIONS}
          />
        </>,
        { marginBottom: SPACING.lg },
      )}

      {/* 상품 검색 → 선정해 담기 (오늘 리스트에만 담을 수 있다) */}
      {!isReadOnly && card(
        <>
          <p style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, marginTop: 0, marginBottom: SPACING.lg }}>
            상품을 검색해 "삽니다"로 선정해 담습니다. 담은 상품은 아래 오늘 목록에 저장되고, 가격은 수시로 수정할 수 있습니다.
          </p>
          <Input
            placeholder="카드명 또는 카드번호 검색"
            prefix={<SearchOutlined />}
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
            // display:block 을 주면 Ant 의 affix wrapper(inline-flex)가 깨져
            // 돋보기 아이콘과 입력칸이 위아래로 갈라진다 — 폭만 지정한다(2026-10-19 감사).
            style={{ maxWidth: 480, width: '100%', marginBottom: SPACING.md }}
            allowClear
          />
          <Table<RegistrableSearchResult>
            rowKey={(r) => r.candidate.shopProductCode}
            columns={searchColumns}
            dataSource={searchResults}
            loading={searching}
            size="small"
            // 좁은 화면에서 표가 페이지를 밀어내지 않고 표 안에서만 가로 스크롤되게 한다.
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: searchDraft.trim() ? '검색 결과가 없습니다' : '카드명이나 카드번호를 입력하세요' }}
          />
        </>,
        { marginBottom: SPACING.lg },
      )}

      {/* 선정된 매입 리스트 (날짜별) */}
      {card(
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING.md, gap: SPACING.md, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
              <span style={{ fontSize: FONT_SIZE.md, fontWeight: 600, color: t.textPrimary }}>
                매입 리스트 ({listings.length}건)
              </span>
              <Select
                value={selectedDate || undefined}
                onChange={setSelectedDate}
                options={availableDates.map((d) => ({ value: d, label: dateLabel(d) }))}
                suffixIcon={<CalendarOutlined />}
                style={{ minWidth: 200 }}
                placeholder="날짜 선택"
              />
              {isReadOnly && <Tag color="default">과거 이력 · 읽기 전용</Tag>}
            </div>
            <AppButton variant="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              A4 인쇄
            </AppButton>
          </div>
          {error ? (
            <AppLoadError message={error.message} error={error} onRetry={retryLoad} />
          ) : (
            <Table<BuybackListing>
              rowKey="id"
              columns={listColumns}
              dataSource={listings}
              loading={loading}
              size="small"
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              locale={{ emptyText: isReadOnly ? '이 날짜에는 담긴 상품이 없습니다.' : '아직 담긴 상품이 없습니다. 위에서 검색해 담아 주세요.' }}
            />
          )}
        </>,
      )}
    </DashboardLayout>
  );
};
